import React, { useCallback, useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { BLOCKS } from '@contentful/rich-text-types'
import RichText from '../../components/RichText'
import Skeleton from '../Skeleton/Skeleton'
import useMatchData from '../../hooks/useMatchData'
import { MatchTitlesAndDescriptions, MatchVariant } from '../../types/match'
import { RichText as RichTextType } from '../../types/post'
import { MatchClasses } from '../../types/contentfulAttributes'

interface MatchDynamicTextProps {
  matchTitlesAndDescriptions: MatchTitlesAndDescriptions
}

const DynamicMatchHeaderAndDesc: React.FC<MatchDynamicTextProps> = ({
  matchTitlesAndDescriptions,
}) => {
  const { classes, cx } = useStyles()
  const [variantToDisplay, setVariantToDisplay] = useState<RichTextType>()
  const { match, isLoading } = useMatchData()

  const { variants, codeClasses } = matchTitlesAndDescriptions
  const getCorrectVariant = useCallback(
    (matchVariants: MatchVariant[]) =>
      matchVariants.find((matchVariant) => {
        if (match?.fit) {
          return matchVariant.variant
            .toLowerCase()
            .includes(match?.fit?.toLowerCase())
        }

        return {}
      })?.text,
    [match?.fit]
  )

  useEffect(() => {
    if (match) {
      setVariantToDisplay(getCorrectVariant(variants))
    }
  }, [match, getCorrectVariant, variants])

  const hasCfClass = (className: string) => codeClasses?.includes(className)

  if (isLoading || !variantToDisplay) {
    return (
      <Box
        className={cx({
          [classes.skeletonContainer]: true,
          [classes.heroSkeletonContainer]: hasCfClass(
            MatchClasses.HeroTitlesAndDescs
          ),
          [classes.threeColSkeletonContainer]: hasCfClass(
            MatchClasses.ThreeColTitlesAndDescs
          ),
        })}
      >
        <Skeleton
          components={Array(5).fill({
            classes: { root: classes.skeletonRoot, wave: classes.wave },
          })}
        />
      </Box>
    )
  }

  return (
    <RichText
      rawBody={variantToDisplay.raw}
      references={variantToDisplay.references}
      options={{
        props: {
          [BLOCKS.PARAGRAPH]: {
            className: classes.p,
          },
        },
      }}
    />
  )
}

const useStyles = makeStyles()((theme) => ({
  p: {
    margin: '.375rem 0',
    paddingLeft: 'calc((100% - 38.5em)/2)',
    paddingRight: 'calc((100% - 38.5em)/2)',
    [theme.breakpoints.down('md')]: {
      paddingLeft: '1rem',
      paddingRight: '1rem',
    },
  },
  skeletonRoot: {
    '&&': {
      backgroundColor: 'rgba(255, 255, 255, .1)',
      borderRadius: '2px',
    },
  },
  wave: {
    '&&': {
      '&:after': {
        background:
          'linear-gradient(269.97deg, rgba(255, 255, 255, .1) .03%, rgba(255, 255, 255, 0) 13.04%, rgba(255, 255, 255, .1) 26.05%)',
      },
    },
  },
  skeletonContainer: {
    width: '100%',
    display: 'grid',
    justifyItems: 'center',
  },
  heroSkeletonContainer: {
    '& > *': {
      paddingBlock: '1rem',
      marginBlock: '.25rem',
      width: '58.47%',

      '@media (max-width: 768px)': {
        width: '100%',
      },

      [theme.breakpoints.up('sm')]: {
        paddingBlock: '1.5rem',
        marginBlock: '.5rem',
      },
    },

    '& > *:nth-child(2)': {
      width: '52.91%',

      '@media (max-width: 768px)': {
        width: '100%',
      },
    },

    '& > *:nth-child(3)': {
      [theme.breakpoints.up('sm')]: {
        display: 'none',
      },
    },

    '& > *:nth-last-child(2)': {
      paddingBlock: '4rem',
      marginTop: '.25rem',
      width: '43.4%',

      '@media (max-width: 768px)': {
        width: '100%',
      },

      [theme.breakpoints.up('sm')]: {
        paddingBlock: '2.25rem',
        marginTop: '.5rem',
      },
    },

    '& > *:last-child': {
      display: 'none',
    },
  },
  threeColSkeletonContainer: {
    '& > *:first-child': {
      width: '100%',
      paddingBlock: '1rem',
      marginBottom: '.5rem',

      [theme.breakpoints.up('sm')]: {
        width: '52.86%',
        paddingBlock: '1rem',
        marginBottom: '.5rem',
      },

      '@media (min-width: 769px)': {
        width: '28.19%',
      },
    },

    '& > *:nth-child(2)': {
      display: 'block',
      width: '100%',
      paddingBlock: '1rem',
      marginBottom: '0.5rem',

      [theme.breakpoints.up('sm')]: {
        display: 'none',
      },
    },

    '& > *:nth-child(3)': {
      width: '100%',
      paddingBlock: '.75rem',
      marginTop: '.5rem',

      [theme.breakpoints.up('sm')]: {
        width: '66.15%',
      },

      '@media (min-width: 769px)': {
        width: '43.33%',
      },
    },

    '& > *:nth-child(4)': {
      width: '100%',
      paddingBlock: '.75rem',
      marginTop: '.5rem',

      [theme.breakpoints.up('sm')]: {
        width: '66.15%',
      },

      '@media (min-width: 769px)': {
        width: '35.56%',
      },
    },

    '& > *:last-child': {
      width: '100%',
      paddingBlock: '.75rem',
      marginTop: '.5rem',

      [theme.breakpoints.up('sm')]: {
        display: 'none',
      },
    },
  },
}))

export default DynamicMatchHeaderAndDesc
